import {useEffect, useState, forwardRef, useRef} from "react";
import {createPortal} from "react-dom";
import type {ReactNode, PropsWithChildren, CSSProperties, ForwardedRef} from "react";

import LoadingAnimation from "sensoteq-react-core/components/LoadingAnimation";
import {Button} from "sensoteq-react-core/forms";
import * as Styled from "./Content.styles";

interface ContentProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  titleContent?: ReactNode;
  loading?: boolean;
  loadingOverlay?: boolean;
  allowFullScreen?: boolean;
  padded?: boolean;
  grow?: boolean;
  sticky?: boolean;
  style?: CSSProperties;
  ignoreChildrenHeight?: boolean;
  border?: boolean;
  bodyStyle?: CSSProperties;
  onFullScreenChange?: (isFullScreen: boolean) => void;
  enableFullScreen?: boolean;
}

const Content = forwardRef<HTMLDivElement, PropsWithChildren<ContentProps>>(
  (props, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      title,
      subtitle,
      loading = false,
      loadingOverlay = false,
      titleContent,
      allowFullScreen = false,
      padded = true,
      grow = false,
      sticky = false,
      style,
      ignoreChildrenHeight = false,
      border = false,
      bodyStyle,
      onFullScreenChange,
      enableFullScreen = false,
      children,
    } = props;

    const [fullScreen, setFullScreen] = useState(enableFullScreen);
    const lastHeightRef = useRef(0);

    const onToggleFullScreen = () => {
      setFullScreen(!fullScreen);
      if (onFullScreenChange) onFullScreenChange(!fullScreen);
    };

    useEffect(() => {
      if (!loading) {
        const el = ref && typeof ref === "object" && "current" in ref ? ref.current : null;
        lastHeightRef.current = el ? el.offsetHeight - 2 : 0;
      }
    }, [loading, ref]);

    let processedChildren = children;
    if (ignoreChildrenHeight) {
      processedChildren = (
        <Styled.ZeroHeightWrapper padded={padded}>
          <div>{children}</div>
        </Styled.ZeroHeightWrapper>
      );
    }

    let content: ReactNode;
    if (loading) {
      content = (
        <Styled.LoadingContainer style={{height: lastHeightRef.current ? `${lastHeightRef.current}px` : "10rem"}}>
          <LoadingAnimation />
        </Styled.LoadingContainer>
      );
    } else {
      content = (
        <Styled.Container
          ref={ref}
          grow={grow}
          sticky={sticky}
          style={style}
          className="bg-content-background"
        >
          {title && (
            <Styled.TitleContainer
              padded={padded}
              border={border}
            >
              <Styled.TitleText>
                <Styled.Title>{title}</Styled.Title>
                {subtitle && <Styled.Subtitle>{subtitle}</Styled.Subtitle>}
              </Styled.TitleText>
              <Styled.TitleActions>
                {titleContent && <Styled.TitleContent>{titleContent}</Styled.TitleContent>}
                {allowFullScreen && (
                  <Styled.TitleIcons>
                    <Button
                      icon={fullScreen ? "fullscreen_exit" : "fullscreen"}
                      onClick={onToggleFullScreen}
                      tooltip={fullScreen ? "Exit full-screen" : "Go full-screen"}
                    />
                  </Styled.TitleIcons>
                )}
              </Styled.TitleActions>
            </Styled.TitleContainer>
          )}
          <Styled.Body
            padded={padded}
            style={bodyStyle}
          >
            {processedChildren}
          </Styled.Body>
          {loadingOverlay && <LoadingAnimation />}
        </Styled.Container>
      );
    }

    if (fullScreen) {
      const fullscreenElement = document.getElementById("fullscreen");
      if (fullscreenElement) {
        return createPortal(<Styled.FullScreenWrapper>{content}</Styled.FullScreenWrapper>, fullscreenElement);
      }
    }

    return content;
  },
);

Content.displayName = "Content";

export default Content;
